import React, { useState, useRef } from 'react';
import { useAttendance } from '../context/AttendanceContext';
import StudentList from './StudentList';
import AttendanceGrid from './AttendanceGrid';
import FileManager from './FileManager';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlus, FiUserPlus, FiCalendar } = FiIcons;

const AttendanceTracker = () => {
  const {
    students,
    currentDate,
    setCurrentDate,
    addStudent,
    addStudentsBulk
  } = useAttendance();

  const [newStudentName, setNewStudentName] = useState('');
  const [bulkNames, setBulkNames] = useState('');
  const [showBulkAdd, setShowBulkAdd] = useState(false);
  const bulkTextareaRef = useRef(null);

  const handleAddStudent = (e) => {
    e.preventDefault();
    if (newStudentName.trim()) {
      addStudent(newStudentName);
      setNewStudentName('');
    }
  };

  const handleBulkAdd = (e) => {
    e.preventDefault();
    if (bulkNames.trim()) {
      addStudentsBulk(bulkNames);
      setBulkNames('');
      setShowBulkAdd(false);
    }
  };

  const toggleBulkAdd = () => {
    setShowBulkAdd(!showBulkAdd);
    if (!showBulkAdd) {
      setTimeout(() => bulkTextareaRef.current?.focus(), 100);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Student Management */}
        <div className="lg:col-span-1 space-y-6">
          {/* File Management */}
          <FileManager />
          
          {/* Add Students */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <SafeIcon icon={FiUserPlus} className="w-5 h-5 mr-2 text-indigo-600" />
              Add Students
            </h2>
            
            <form onSubmit={handleAddStudent} className="space-y-3">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newStudentName}
                  onChange={(e) => setNewStudentName(e.target.value)}
                  placeholder="Enter student name"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                />
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors"
                >
                  <SafeIcon icon={FiPlus} className="w-4 h-4" />
                </button>
              </div>
            </form>

            <div className="mt-4">
              <button
                onClick={toggleBulkAdd}
                className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
              >
                {showBulkAdd ? 'Single Add' : 'Bulk Add'}
              </button>
            </div>

            {showBulkAdd && (
              <form onSubmit={handleBulkAdd} className="mt-4 space-y-3">
                <textarea
                  ref={bulkTextareaRef}
                  value={bulkNames}
                  onChange={(e) => setBulkNames(e.target.value)}
                  placeholder="Enter names, one per line"
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none resize-none"
                />
                <button
                  type="submit"
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors"
                >
                  Add All Students
                </button>
              </form>
            )}
          </div>

          {/* Student List */}
          <StudentList />
        </div>

        {/* Right Column - Attendance */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <SafeIcon icon={FiCalendar} className="w-5 h-5 mr-2 text-indigo-600" />
                Daily Attendance
              </h2>
              <input
                type="date"
                value={currentDate}
                onChange={(e) => setCurrentDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
              />
            </div>

            {students.length === 0 ? (
              <div className="text-center py-12">
                <SafeIcon icon={FiUserPlus} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No students added yet. Add some students to get started!</p>
              </div>
            ) : (
              <AttendanceGrid />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceTracker;